import {createAsyncThunk} from "@reduxjs/toolkit";
import {toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Include helper methods for API calls
import {
    addNewContract as addNewContractApi,
    deleteContract as deleteContractApi,
    getContractById as getContractByIdApi,
    getContractByIdFull as getContractByIdFullApi,
    getContracts as getContractsApi,
    updateContract as updateContractApi,
    updateContractFinancials as updateContractFinancialsApi,
    createContract as createContractApi,
    updatePaymentBulk as updatePaymentBulkApi
} from "../../helpers/fakebackend_helper";

const formatContractError = (error, fallback) => {
    const details = error?.details;
    if (details && typeof details === "object") {
        const fieldMessages = Object.entries(details)
            .map(([field, messages]) => {
                const text = Array.isArray(messages) ? messages.join(" ") : messages;
                return `${field}: ${text}`;
            });
        if (fieldMessages.length) return fieldMessages.join(" | ");
    }
    return error?.message || fallback;
};

// Fetch Contracts
export const getContracts = createAsyncThunk(
    "contracts",
    async (queryParams) => {
        try {
            return await getContractsApi(queryParams);
        } catch (error) {
            toast.error("Failed to fetch contracts", {autoClose: 3000});
            throw error;
        }
    });

// Fetch a Single Contract by ID
export const getContractById = createAsyncThunk(
    "contracts/getContractById",
    async (id) => {
        try {
            // API call to get a single contract by ID
            return await getContractByIdApi(id);
        } catch (error) {
            toast.error("Failed to fetch contract by ID", {autoClose: 3000});
            throw error;
        }
    }
);

export const getContractByIdFull = createAsyncThunk(
    "contracts/getContractByIdFull",
    async (id) => {
        try {
            return await getContractByIdFullApi(id);
        }
        catch (error) {
            toast.error("Failed to fetch contract by ID", {autoClose: 3000});
            throw error;
        }
    }
)


// Create contract api with payments and settlemetn prices
export const addNewContract = createAsyncThunk(
    "contracts/addNewContract",
    async (contract) => {
        try {
            const response = await addNewContractApi(contract);
            toast.success("Contract Added Successfully", {autoClose: 3000});
            return response;
        } catch (error) {
            toast.error(formatContractError(error, "Failed to add contract"), {autoClose: 5000});
            throw error;
        }
    }
);

// Add New Contract
export const createContract = createAsyncThunk(
    "contracts/createContract",
    async (contract) => {
        try {
            const response = await createContractApi(contract);
            toast.success("Contract Created Successfully", {autoClose: 3000});
            return response;
        }
        catch (error) {
            toast.error("Failed to create contract", {autoClose: 3000});
            throw error;
        }
    }
)

// Update Contract
export const updateContract = createAsyncThunk(
    "contracts/updateContract",
    async (contract) => {
        try {
            const response = await updateContractApi(contract);
            toast.success("Contract Updated Successfully", {autoClose: 3000});
            return response;
        } catch (error) {
            toast.error(error.message || "Failed to update contract", {autoClose: 3000});
            throw error;
        }
    }
);

// Delete Contract
export const deleteContract = createAsyncThunk(
    "contracts/deleteContract",
    async (contractId) => {
        try {
            const response = await deleteContractApi(contractId);
            toast.success("Contract Deleted Successfully", {autoClose: 3000});
            return {contractId, ...response};
        } catch (error) {
            toast.error("Failed to delete contract", {autoClose: 3000});
            throw error;
        }
    }
);


// Update a contract's settlement prices (per unit) and payments together
export const updateContractFinancials = createAsyncThunk(
  "contracts/updateContractFinancials",
  async ({ contractId, payload }, { rejectWithValue }) => {
    try {
      const response = await updateContractFinancialsApi(contractId, payload);
      toast.success("Contract Updated Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      // Surfaced inline in the edit-payments modal rather than as a toast, so
      // reject with the plain message instead of the raw (unserializable) error.
      const message = error?.details?.error?.message || error?.message || "Failed to update contract";
      return rejectWithValue(message);
    }
  }
);

// Updaet Payments Bulk
export const updatePaymentBulk = createAsyncThunk(
  "payments/updatePaymentBulk",
  async (payments) => {
    try {
      const response = await updatePaymentBulkApi(payments);
      toast.success("Payments Updated Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Failed to add payment", { autoClose: 3000 });
      throw error;
    }
  }
);
